/* import __COLOCATED_TEMPLATE__ from './recipient-manager.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-experience */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type Session from 'embercom/services/session';
import { ReplyChannelType, type ReplyChannel } from 'embercom/objects/inbox/composer-pane';
import type UserSummary from 'embercom/objects/inbox/user-summary';
import type { NewConversation } from 'embercom/objects/inbox/conversation';
import type Conversation from 'embercom/objects/inbox/conversation';
import { Channel } from 'embercom/models/data/inbox/channels';
import { trackedReset } from 'tracked-toolbox';
import { Recipients } from 'embercom/lib/composer/recipients';
import { isEmpty } from '@ember/utils';

interface RecipientManagerArgs {
  conversation: Conversation | NewConversation;
  recipients: Recipients;
  originalRecipients: Recipients;
  setRecipientErrors: (
    recipientsWithErrors: { recipient: UserSummary; recipientError: string | void }[],
  ) => void;
  isCreatingConversation?: boolean;
  replyChannel: ReplyChannel;
  replyToData?: Array<UserSummary>;
  isSideConversationComposer?: boolean;
  onRecipientChange: (recipientsList: Recipients) => void;
}

export default class RecipientManagerComponent extends Component<RecipientManagerArgs> {
  @service declare session: Session;
  @service declare intercomEventService: $TSFixMe;
  @trackedReset('args.recipients') ccRecipients = this.args.recipients?.cc || [];
  @trackedReset('args.recipients') bccRecipients = this.args.recipients?.bcc || [];
  @trackedReset('args.recipients') toRecipients = this.args.recipients?.to || [];
  @trackedReset('args.conversation') showCc = this.showCcByDefault();
  @trackedReset('args.conversation') showBcc = this.showBccByDefault();
  @tracked focusStates = {
    cc: false,
    bcc: false,
  };
  @trackedReset('args.originalRecipients') originalToRecipients =
    this.args.originalRecipients?.to || [];
  @trackedReset('args.originalRecipients') originalCcRecipients =
    this.args.originalRecipients?.cc || [];
  @trackedReset('args.originalRecipients') originalBccRecipients =
    this.args.originalRecipients?.bcc || [];

  showCcByDefault() {
    let anyExistingCcRecipients = this.ccRecipients.length > 0;
    let anyOriginalCcRecipients = this.originalCcRecipients.length > 0;
    return this.isEmailCurrentChannel && (anyExistingCcRecipients || anyOriginalCcRecipients);
  }

  showBccByDefault() {
    let anyExistingBccRecipients = this.bccRecipients.length > 0;
    let anyOriginalBccRecipients = this.originalBccRecipients.length > 0;
    return this.isEmailCurrentChannel && (anyExistingBccRecipients || anyOriginalBccRecipients);
  }

  sortEmailOnlyLast(a: UserSummary, b: UserSummary) {
    if (a.id && !b.id) {
      return -1;
    }
    if (!a.id && b.id) {
      return 1;
    }
    return 0;
  }

  updateRecipients() {
    if (!this.args.onRecipientChange) {
      return;
    }
    let sortedToRecipients = this.toRecipients.sort(this.sortEmailOnlyLast);
    let sortedCCRecipients = this.ccRecipients.sort(this.sortEmailOnlyLast);
    let sortedBCCRecipients = this.bccRecipients.sort(this.sortEmailOnlyLast);
    let updatedRecipients = new Recipients(
      sortedToRecipients,
      sortedCCRecipients,
      sortedBCCRecipients,
    );

    this.args.onRecipientChange(updatedRecipients);
  }

  enableFocusState(field: keyof typeof this.focusStates) {
    this.focusStates[field] = true;
    Object.keys(this.focusStates).forEach((key) => {
      if (key !== field) {
        this.focusStates[key as keyof typeof this.focusStates] = false;
      }
    });
  }

  get isEmailCurrentChannel() {
    let currentChannel = (this.args.conversation as Conversation)?.channel.current;
    return currentChannel
      ? currentChannel === Channel.Email
      : this.args.replyChannel.type === ReplyChannelType.Email;
  }

  //TODO Can this and the below method be cleaned up? Do we need to manually call updateRecipients?
  @action
  onToRecipientChange(recipientsList: UserSummary[]) {
    this.toRecipients = recipientsList;
    this.updateRecipients();
  }

  @action
  onCcRecipientChange(recipientsList: UserSummary[]) {
    this.ccRecipients = recipientsList;
    this.updateRecipients();
  }

  @action
  onBccRecipientChange(recipientsList: UserSummary[]) {
    this.bccRecipients = recipientsList;
    this.updateRecipients();
  }

  @action
  triggerCc() {
    this.enableFocusState('cc');
    this.showCc = true;

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'cc_label',
      section: 'composer',
      conversation_id: this.args.conversation?.id ?? null,
      is_side_conversation: this.args.isSideConversationComposer,
    });
  }

  @action
  triggerBcc() {
    this.enableFocusState('bcc');
    this.showBcc = true;

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'bcc_label',
      section: 'composer',
      conversation_id: this.args.conversation?.id ?? null,
      is_side_conversation: this.args.isSideConversationComposer,
    });
  }

  @action
  handleBlur(): void {
    if (isEmpty(this.ccRecipients) && isEmpty(this.originalCcRecipients)) {
      this.showCc = false;
    }
    if (isEmpty(this.bccRecipients) && isEmpty(this.originalBccRecipients)) {
      this.showBcc = false;
    }
    this.focusStates = {
      cc: false,
      bcc: false,
    };
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Composer::RecipientManager': typeof RecipientManagerComponent;
    'inbox2/composer/recipient-manager': typeof RecipientManagerComponent;
  }
}
